/**
 * NESW Bundles V2 — behavior for the two PDP bundle surfaces.
 *
 *   snippets/nesw-bundle-tiers-v2.liquid    → [data-btier]  buy-more ladder
 *   snippets/nesw-bundle-pairing-v2.liquid  → [data-bpair]  pair offer card
 *
 * Both snippets ship their own <script src> tag, so this file can execute
 * twice on one page. Everything below is idempotent: the module guards on
 * window.__neswBundlesV2 and every root guards on its own dataset flag.
 *
 * DISPLAY ONLY. Pricing is decided by the Ignis bundle discount function at
 * checkout — every number here is a prediction of what that function will do,
 * computed with the published display math (contract v1, all cents integers):
 *
 *   tier   amount  = cents off EACH unit          percent = whole percent
 *   pair   amount  = cents off the PAIR TOTAL, split in proportion to unit
 *                    price, remainder to the second line so nothing is lost
 *   clamps  percent capped at 90 (negative → 0); discount never exceeds the
 *           unit price
 *
 * Cart protocol: a pair is added as ONE batched /cart/add.js call through
 * window.neswAddToCartItems, both lines carrying the same fresh `_bundle_id`
 * plus `_bundle_kind: 'pair'`. Tier lines carry no properties at all — any N
 * units of a product pool automatically.
 *
 * APPEARANCE. Colours, radii, layout and the button wording come from the
 * Ignis bundles appearance config and are applied by the snippets, in Liquid
 * and in CSS. Exactly one of them reaches this file: the pair CTA is rebuilt
 * here every time the totals move, so the wording has to be rebuilt with it,
 * and it arrives on the root as data-cta-label. Everything else is presented
 * without JS on purpose, so nothing about how a card LOOKS depends on this
 * script having run.
 */
(function () {
  if (window.__neswBundlesV2) return;
  window.__neswBundlesV2 = true;

  /* ---------------------------------------------------------------- utils */

  var AMT = '{' + '{amount}' + '}';

  /* Local money formatter (same token set as assets/money-format-handler.js).
     Deliberately NOT Shopify.formatMoney: that file is deferred later in the
     document than these widgets, so it is not guaranteed to exist at init. */
  function makeMoney(format) {
    var fmt = format || ('$' + AMT);
    function withDelims(cents, precision, thousands, decimal) {
      var n = (cents / 100).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    return function (cents) {
      cents = Math.round(cents || 0);
      var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      var value;
      switch (m && m[1]) {
        case 'amount_no_decimals': value = withDelims(cents, 0, ',', '.'); break;
        /* No ".00" on whole amounts (2026-09-22): $48, but still $81.60 when there are cents. */
        case 'amount_with_comma_separator': value = withDelims(cents, cents % 100 === 0 ? 0 : 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
        case 'amount': value = withDelims(cents, cents % 100 === 0 ? 0 : 2, ',', '.'); break;
        default: return '$' + (cents / 100).toFixed(2);
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    };
  }

  function json(el, attr, fallback) {
    try { return JSON.parse(el.getAttribute(attr)); } catch (e) { return fallback; }
  }
  function num(v) { var n = parseInt(v, 10); return isNaN(n) ? 0 : n; }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function track(name, payload) {
    try { if (window.pxl && window.pxl.track) window.pxl.track(name, payload); } catch (e) {}
  }
  function newBundleId() {
    return 'ig-pair-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* Cents off ONE unit for a tier. `unit` is the price the shopper pays today
     (post store sale), so a percent tier lands on the same base the pricing
     function uses when sale stacking is on. */
  /* Whole dollars (2026-09-22): both blocks carry data-whole-dollars from the theme
     setting; after a percent the unit price is rounded DOWN to the dollar, the same
     rule the discount function applies in the cart. */
  var WHOLE = !!document.querySelector('[data-whole-dollars="1"]');
  function wholeOff(unit, off) {
    if (!WHOLE) return off;
    var net = Math.max(0, unit - off);
    return unit - Math.floor(net / 100) * 100;
  }
  function tierOffPerUnit(unit, type, value) {
    var off;
    if (type === 'percent') {
      var pct = Math.max(0, Math.min(90, value));
      off = wholeOff(unit, Math.round(unit * pct / 100));
    } else {
      off = value;
    }
    return Math.max(0, Math.min(off, unit));
  }

  /* Cents off each side of a pair. Returns [offA, offB]. */
  function pairOff(unitA, unitB, type, value) {
    var offA, offB;
    if (type === 'percent') {
      var pct = Math.max(0, Math.min(90, value));
      offA = wholeOff(unitA, Math.round(unitA * pct / 100));
      offB = wholeOff(unitB, Math.round(unitB * pct / 100));
    } else {
      var total = unitA + unitB;
      offA = total > 0 ? Math.round(value * unitA / total) : 0;
      offB = value - offA;
    }
    offA = Math.max(0, Math.min(offA, unitA));
    offB = Math.max(0, Math.min(offB, unitB));
    return [offA, offB];
  }

  /* Run fn whenever the product page's colour or size pick changes (N-47: this
     replaced two 400ms setInterval pollers that ran for the life of every product
     page). Every path that changes a pick (a shopper's click, the landing
     auto-size, ?variant= deep links, the floating bar's tray, which presses the
     page's own pills) toggles the selected class on a pill, so ONE observer on
     the pills' common ancestor sees all of them. fn runs one frame later, after
     the theme has written #ultimate-selected-variant (and again 450ms later, and
     on 'nesw:variant' from writers that move no pill); callers compare against
     their last value, so a burst of mutations costs one cheap read. */
  var pickListeners = [];
  var pickObserved = false;
  function onPdpPick(fn) {
    pickListeners.push(fn);
    if (pickObserved) return;
    var pills = document.querySelectorAll('.w-commerce-commerceaddtocartoptionpill');
    if (!pills.length || !window.MutationObserver) return;
    var host = pills[0].parentElement;
    while (host && host !== document.body) {
      var all = true;
      for (var i = 0; i < pills.length; i++) { if (!host.contains(pills[i])) { all = false; break; } }
      if (all) break;
      host = host.parentElement;
    }
    if (!host) return;
    pickObserved = true;
    var queued = false, late = null;
    function runAll() { for (var j = 0; j < pickListeners.length; j++) pickListeners[j](); }
    function kick() {
      if (!queued) { queued = true; requestAnimationFrame(function () { queued = false; runAll(); }); }
      /* The face poller in product-section-1 corrects #ultimate-selected-variant up to 350ms
         after a pill paints; look once more after that window (review 2026-10-09). */
      clearTimeout(late); late = setTimeout(runAll, 450);
    }
    new MutationObserver(kick).observe(host, { attributes: true, subtree: true, attributeFilter: ['class'] });
    /* Writers that move the variant with no pill class change announce it (product-section-1:
       lockVariantForAdd and the face poller's correction). */
    document.addEventListener('nesw:variant', kick);
    /* A pick that painted before this script booted (the landing auto-size) has no mutation left
       to see: look once now and once after the face poller's window. */
    kick();
  }

  /* The PDP's current variant id. Same hidden input the theme's own ATC price
     poller reads (snippets/product-variants-data.liquid). */
  function pdpVariantId() {
    var el = document.getElementById('ultimate-selected-variant');
    return el ? num(el.value) : 0;
  }

  /* ------------------------------------------------------- 1. tier ladder */

  function initTiers(root) {
    if (root.dataset.bxInit) return;
    root.dataset.bxInit = '1';

    var money = makeMoney(root.dataset.moneyFormat);
    var showPercent = root.dataset.display === 'percent';
    var saleCents = num(root.dataset.saleCents);
    var freeShipCents = num(root.dataset.freeShipCents) || 0;
    var unitWord = root.getAttribute('data-unit-word') || 'each';
    var variants = json(root, 'data-variants', []) || [];
    var cards = root.querySelectorAll('[data-btier-card]');
    var note = root.querySelector('[data-btier-note]');
    var msg = root.querySelector('[data-bx-msg]');
    var lastId = null;
    /* The buy form this ladder feeds. The ladder sits inside it (V4), but
       fall back to the page's product form so a moved snippet still works. */
    var form = root.closest('form[action="/cart/add"]') || document.querySelector('form[action="/cart/add"]');
    var qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
    var selectedQty = 1;

    function currentVariant() {
      var id = pdpVariantId();
      var found = null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) { found = variants[i]; break; }
      }
      return found || variants[0] || null;
    }

    /* The cart-aware unit price for buying `qty` of this variant (2026-09-25): once the Ignis quote is
       ready (assets/nesw-offer-bar.js) it is the whole answer, including "nothing off", because it
       counts what is already in the cart the way checkout does. Before that, the ladder's own tier. */
    var pid = root.getAttribute('data-product-id');
    function unitAfter(v, qty, unitGross, unitNow, card) {
      if (window.neswTierReady && window.neswTierReady() && v && !saleCents) {
        var q = window.neswTierQuote({ productId: pid, variantId: v.id, cents: unitGross, qty: qty });
        return q ? q.cents : unitGross;
      }
      var type = card ? (card.dataset.discountType || '') : '';
      var value = card ? num(card.dataset.discountValue) : 0;
      var off = qty > 1 && type ? tierOffPerUnit(unitNow, type, value) : 0;
      return unitNow - off;
    }

    function tierFor(qty) {
      for (var i = 0; i < cards.length; i++) {
        if (num(cards[i].dataset.qty) === qty) return cards[i];
      }
      return null;
    }

    /* V4 — the selection is published for the theme's Add to Cart face and
       submit path: window.__neswTier = { qty, priceHTML, label }. The pill's
       own poller (product-section-1.liquid) reads it on every render, and
       product-add-to-cart.js reads the hidden quantity input on submit. */
    function publish() {
      var v = currentVariant();
      var unitGross = v ? v.price : num(root.dataset.basePrice);
      var unitNow = Math.max(0, unitGross - saleCents);
      var card = tierFor(selectedQty);
      var wasTotal = unitGross * selectedQty;
      var nowTotal = unitAfter(v, selectedQty, unitGross, unitNow, card) * selectedQty;
      var priceHTML = (wasTotal > nowTotal)
        ? '<s>' + money(wasTotal) + '</s> ' + money(nowTotal)
        : money(nowTotal);
      window.__neswTier = {
        qty: selectedQty,
        priceHTML: selectedQty > 1 ? priceHTML : '',
        label: selectedQty > 1 ? 'Add ' + selectedQty + ' to cart' : 'Add to cart'
      };
      if (qtyInput) qtyInput.value = String(selectedQty);
      if (typeof window.__neswAtcPillRender === 'function') window.__neswAtcPillRender();
      try { document.dispatchEvent(new CustomEvent('nesw:tier', { detail: window.__neswTier })); } catch (e) {}
    }

    function select(qty) {
      selectedQty = qty;
      each(cards, function (card) {
        var on = num(card.dataset.qty) === qty;
        card.classList.toggle('is-selected', on);
        card.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      publish();
    }

    /* Jay 2026-10-01: with items already in the cart every box can reach the same tier, so all three
       showed one price and read as broken. The boxes stay honest (cart-aware) and the eyebrow says why. */
    var eyebrow = root.querySelector('.nesw-bx__eyebrow');
    var eyebrowDefault = eyebrow ? eyebrow.textContent : '';
    var tierCart = null;
    function cartPool() {
      var bar = document.querySelector('[data-nesw-offer-bar]');
      tierCart = tierCart || window.__neswTierCart || null;
      if (!tierCart || !bar || typeof window.__neswOfferCount !== 'function') return 0;
      try { return window.__neswOfferCount(tierCart, bar) || 0; } catch (e) { return 0; }
    }

    function render() {
      var v = currentVariant();
      var units = [];
      var unitGross = v ? v.price : num(root.dataset.basePrice);
      var unitNow = Math.max(0, unitGross - saleCents);
      var buyable = !!(v && v.available);

      each(cards, function (card) {
        var qty = num(card.dataset.qty);
        var wasTotal = unitGross * qty;
        var nowTotal = unitAfter(v, qty, unitGross, unitNow, card) * qty;
        var save = wasTotal - nowTotal;

        var nowEl = card.querySelector('[data-btier-now]');
        var saveEl = card.querySelector('[data-btier-save]');
        var eachEl = card.querySelector('[data-btier-each]');
        var totalEl = card.querySelector('[data-btier-total]');
        var shipEl = card.querySelector('[data-btier-ship]');

        units.push({ unit: Math.round(nowTotal / qty), save: save });
        if (nowEl) nowEl.textContent = money(nowTotal / qty);
        if (eachEl) eachEl.textContent = unitWord;
        if (totalEl) totalEl.textContent = money(nowTotal) + ' total';
        /* The mark only where the tier CROSSES the threshold: a unit already over
           it would light every card, which says nothing (Jay 2026-09-21). */
        if (shipEl) shipEl.hidden = !(freeShipCents > 0 && unitNow < freeShipCents && nowTotal >= freeShipCents);
        if (saveEl) {
          if (save <= 0) {
            saveEl.textContent = '';
          } else if (showPercent) {
            saveEl.textContent = 'Save ' + Math.round(save / wasTotal * 100) + '%';
          } else {
            saveEl.textContent = 'Save ' + money(save);
          }
        }
        card.disabled = !buyable;
      });

      if (eyebrow) {
        var inCart = cartPool();
        var same = units.length > 1 && units.every(function (u) { return u.unit === units[0].unit; });
        if (inCart <= 0) eyebrow.textContent = eyebrowDefault;
        else if (same && units[0].save > 0) eyebrow.textContent = 'Your cart already unlocks the top saving. Prices include it.';
        else eyebrow.textContent = 'Prices include the ' + inCart + (inCart === 1 ? ' item' : ' items') + ' in your cart.';
      }

      if (note) {
        note.textContent = buyable ? '' : 'This size is sold out. Pick another to bundle.';
        note.hidden = buyable;
      }
      if (msg) msg.textContent = '';
      publish();
    }

    each(cards, function (card) {
      card.addEventListener('click', function () {
        if (card.disabled) return;
        select(num(card.dataset.qty) || 1);
        track('bundle_tier_selected', { bundle_kind: 'tier', units: num(card.dataset.qty) || 1 });
      });
    });

    render();
    /* The cart changed (or was first read): what each box costs may have too. */
    document.addEventListener('nesw:tiercart', function (e) { tierCart = (e && e.detail && e.detail.cart) || tierCart; render(); });
    /* Ad landing templates may open on a tier (settings.landing_preselect_qty,
       written as data-preselect by the snippet): the ad sold the pack, so the
       page opens on the pack. */
    var preselect = num(root.dataset.preselect);
    if (preselect > 1 && tierFor(preselect) && !tierFor(preselect).disabled) select(preselect);
    /* Follow the PDP's variant: re-render when a pick changes it. */
    onPdpPick(function () {
      var id = pdpVariantId();
      if (id !== lastId) { lastId = id; render(); }
    });
  }

  /* --------------------------------------------------------- 2. pair card */

  function initPair(root) {
    if (root.dataset.bxInit) return;
    root.dataset.bxInit = '1';

    var money = makeMoney(root.dataset.moneyFormat);
    var dType = root.dataset.discountType || 'amount';
    var dValue = num(root.dataset.discountValue);
    var cta = root.querySelector('[data-bpair-cta]');
    var msg = root.querySelector('[data-bx-msg]');
    /* The merchant's own button wording, from the Ignis appearance config.
       The snippet already rendered it server-side; this is the same string,
       for the rebuild below. 🔴 It is merchant free text, so it is only ever
       written as a TEXT NODE, never through innerHTML. */
    var ctaLabel = root.getAttribute('data-cta-label') || 'Add bundle to cart';

    var rows = Array.prototype.map.call(root.querySelectorAll('[data-bpair-row]'), function (el) {
      return {
        el: el,
        role: el.dataset.role,
        saleCents: num(el.dataset.saleCents),
        basePrice: num(el.dataset.basePrice),
        hasColor: el.dataset.hasColor === '1',
        hasSize: el.dataset.hasSize === '1',
        variants: json(el, 'data-variants', []) || [],
        color: null,
        size: null,
        variant: null
      };
    });

    function anyAvailable(row, filter) {
      for (var i = 0; i < row.variants.length; i++) {
        var v = row.variants[i];
        if (!v.available) continue;
        if (filter(v)) return true;
      }
      return false;
    }

    /* The buyable variant for this row's current picks, or null. Every axis the
       product actually has must be chosen; an unavailable match is not buyable.
       A stray third option resolves to its first available value. */
    function resolve(row) {
      if (row.hasColor && !row.color) return null;
      if (row.hasSize && !row.size) return null;
      for (var i = 0; i < row.variants.length; i++) {
        var v = row.variants[i];
        if (row.hasColor && v.color !== row.color) continue;
        if (row.hasSize && v.size !== row.size) continue;
        if (v.available) return v;
      }
      return null;
    }

    /* Swap the row image to the chosen color's product shot. */
    function setImage(row) {
      var img = row.el.querySelector('[data-bpair-img]');
      if (!img || !row.color) return;
      for (var i = 0; i < row.variants.length; i++) {
        if (row.variants[i].color === row.color && row.variants[i].img) {
          img.src = row.variants[i].img;
          return;
        }
      }
    }

    function paintOptions(row) {
      each(row.el.querySelectorAll('[data-bpair-sw]'), function (btn) {
        var c = btn.getAttribute('data-color');
        var live = anyAvailable(row, function (v) { return v.color === c; });
        btn.classList.toggle('is-out', !live);
        btn.classList.toggle('is-on', c === row.color);
        btn.setAttribute('aria-pressed', c === row.color ? 'true' : 'false');
      });
      each(row.el.querySelectorAll('[data-bpair-size]'), function (btn) {
        var s = btn.getAttribute('data-size');
        var live = anyAvailable(row, function (v) {
          return v.size === s && (!row.hasColor || !row.color || v.color === row.color);
        });
        btn.classList.toggle('is-out', !live);
        btn.classList.toggle('is-on', s === row.size);
        btn.setAttribute('aria-pressed', s === row.size ? 'true' : 'false');
      });
    }

    function update() {
      rows.forEach(function (row) {
        row.variant = resolve(row);
        paintOptions(row);
        var dead = !anyAvailable(row, function () { return true; });
        row.el.classList.toggle('is-dead', dead);
        var cue = row.el.querySelector('[data-bpair-cue]');
        if (cue) {
          if (dead) { cue.hidden = false; cue.textContent = 'Sold out'; }
          else if (row.variant) { cue.hidden = true; }
          else if (row.hasSize && !row.size) { cue.hidden = false; cue.textContent = 'Select a size'; }
          else { cue.hidden = false; cue.textContent = 'That combination is sold out'; }
        }
      });

      var a = rows[0], b = rows[1];
      var grossA = a.variant ? a.variant.price : a.basePrice;
      var grossB = b.variant ? b.variant.price : b.basePrice;
      var nowA = Math.max(0, grossA - a.saleCents);
      var nowB = Math.max(0, grossB - b.saleCents);
      var offs = pairOff(nowA, nowB, dType, dValue);

      var gross = grossA + grossB;
      var net = (nowA - offs[0]) + (nowB - offs[1]);
      var save = gross - net;

      setPrice(a, grossA, nowA - offs[0]);
      setPrice(b, grossB, nowB - offs[1]);

      var wasEl = root.querySelector('[data-bpair-was]');
      var nowEl = root.querySelector('[data-bpair-now]');
      var pillEl = root.querySelector('[data-bpair-pill]');
      if (wasEl) wasEl.textContent = save > 0 ? money(gross) : '';
      if (nowEl) nowEl.textContent = money(net);
      if (pillEl) pillEl.textContent = save > 0 ? 'You save ' + money(save) : '';

      var ready = !!(a.variant && b.variant);
      cta.disabled = !ready;
      if (ready) paintCta(gross, net, save);
      else cta.textContent = ctaPrompt();
    }

    /* The wording, then the struck total, then the price: the same line the
       snippet rendered, rebuilt as nodes. textContent for the merchant's
       string is the escaping — an innerHTML template here would run whatever
       a merchant typed into the appearance editor on every shopper's page. */
    function paintCta(gross, net, save) {
      cta.textContent = ctaLabel + ' - ';
      if (save > 0) {
        var was = document.createElement('s');
        was.textContent = money(gross);
        cta.appendChild(was);
        cta.appendChild(document.createTextNode(' '));
      }
      var now = document.createElement('b');
      now.textContent = money(net);
      cta.appendChild(now);
    }

    function ctaPrompt() {
      var deadRow = false, unpicked = 0, soldOut = false;
      rows.forEach(function (row) {
        if (!anyAvailable(row, function () { return true; })) { deadRow = true; return; }
        if (row.variant) return;
        if ((row.hasSize && !row.size) || (row.hasColor && !row.color)) unpicked++;
        else soldOut = true;
      });
      if (deadRow) return 'Bundle unavailable right now';
      if (unpicked > 0) return unpicked > 1 ? 'Select both sizes' : 'Select a size';
      if (soldOut) return 'Selected size sold out';
      return 'Select your options';
    }

    function setPrice(row, gross, final) {
      var el = row.el.querySelector('[data-bpair-price]');
      if (!el) return;
      el.innerHTML = final < gross
        ? '<s>' + money(gross) + '</s><b>' + money(final) + '</b>'
        : money(gross);
    }

    /* --- option clicks --- */
    rows.forEach(function (row) {
      each(row.el.querySelectorAll('[data-bpair-sw]'), function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-out')) return;
          row.userTouched = true; /* a deliberate pick in the block stops it following the page */
          row.color = btn.getAttribute('data-color');
          /* Keep the size if it still exists in the new color. */
          if (row.size && !anyAvailable(row, function (v) {
            return v.size === row.size && v.color === row.color;
          })) row.size = null;
          setImage(row);
          update();
        });
      });
      each(row.el.querySelectorAll('[data-bpair-size]'), function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-out')) return;
          row.userTouched = true;
          row.size = btn.getAttribute('data-size');
          update();
        });
      });
    });

    /* --- add both lines in ONE call, one shared fresh _bundle_id --- */
    cta.addEventListener('click', function () {
      if (cta.disabled) return;
      var a = rows[0], b = rows[1];
      if (!a.variant || !b.variant) return;
      if (msg) msg.textContent = '';

      var bundleId = newBundleId();
      var props = { _bundle_id: bundleId, _bundle_kind: 'pair' };
      var items = [
        { id: a.variant.id, quantity: 1, properties: props },
        { id: b.variant.id, quantity: 1, properties: props }
      ];

      var before = cta.innerHTML;
      cta.disabled = true;
      cta.textContent = 'Adding';
      window.neswAddToCartItems(items, { openDrawer: true })
        .then(function () {
          track('bundle_add_to_cart', { bundle_kind: 'pair', lines: items.length });
          cta.innerHTML = before;
          cta.disabled = false;
        })
        .catch(function () {
          cta.innerHTML = before;
          cta.disabled = false;
          if (msg) msg.textContent = 'Could not add the bundle. An item may be out of stock.';
        });
    });

    /* --- initial selection ---------------------------------------------
       Page-product row starts on whatever the shopper is already looking at,
       read from the PDP controls (read only, never clicked, so the gallery
       sync above is untouched). Everything else starts on its first
       available variant. */
    function handleize(s) {
      return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }
    function pdpColorHandle() {
      var sw = document.querySelector('.product-color-swatch.w--ecommerce-pill-selected');
      return sw ? (sw.getAttribute('data-option-name') || '').trim().toLowerCase() : '';
    }
    function pdpSize() {
      var p = document.querySelector('.add-to-cart-section .w-commerce-commerceaddtocartoptionpill:not(.product-color-swatch).w--ecommerce-pill-selected');
      return p ? (p.textContent || '').trim() : '';
    }

    rows.forEach(function (row) {
      var seed = null;
      for (var i = 0; i < row.variants.length; i++) {
        if (row.variants[i].available) { seed = row.variants[i]; break; }
      }
      if (row.hasColor) row.color = seed ? seed.color : null;
      if (row.hasSize) row.size = seed ? seed.size : null;

      if (row.role === 'page') {
        var pc = pdpColorHandle();
        if (row.hasColor && pc) {
          for (var j = 0; j < row.variants.length; j++) {
            if (handleize(row.variants[j].color) === pc) { row.color = row.variants[j].color; break; }
          }
        }
        var ps = pdpSize();
        if (row.hasSize && ps) {
          for (var k = 0; k < row.variants.length; k++) {
            if (row.variants[k].size === ps && row.variants[k].available) { row.size = ps; break; }
          }
        }
      }
      setImage(row);
    });

    update();

    /* --- follow the page (review 2026-09-22) ---------------------------------
       The seed above runs at boot, which can be BEFORE the landing auto-size
       click has painted the preferred size, and nothing re-read the page after
       a shopper changed size or colour: the block sat on S / XS while the page
       showed L, and "Add bundle to cart" shipped the wrong size. The page
       product's row now follows the page's selected colour and size until the
       shopper picks inside the block; the other rows take the same size letter
       when they have it in their colour. The framework paints the pills without
       a usable event, so this follows the pills' selected class (onPdpPick). */
    var pageRow = null;
    rows.forEach(function (r) { if (r.role === 'page') pageRow = r; });
    if (pageRow) {
      var lastSig = '';
      var followPage = function () {
        var pc = pdpColorHandle(), ps = pdpSize();
        var sig = pc + '|' + ps;
        if (sig === lastSig) return;
        lastSig = sig;
        var changed = false;
        if (!pageRow.userTouched) {
          if (pageRow.hasColor && pc) {
            for (var j = 0; j < pageRow.variants.length; j++) {
              if (handleize(pageRow.variants[j].color) === pc) {
                if (pageRow.color !== pageRow.variants[j].color) { pageRow.color = pageRow.variants[j].color; changed = true; }
                break;
              }
            }
          }
          if (pageRow.hasSize && ps) {
            for (var k = 0; k < pageRow.variants.length; k++) {
              var pv = pageRow.variants[k];
              if (pv.size === ps && pv.available && (!pageRow.hasColor || !pageRow.color || pv.color === pageRow.color)) {
                if (pageRow.size !== ps) { pageRow.size = ps; changed = true; }
                break;
              }
            }
          }
          if (changed) setImage(pageRow);
        }
        if (ps) {
          rows.forEach(function (r) {
            if (r === pageRow || r.userTouched || !r.hasSize || r.size === ps) return;
            for (var m = 0; m < r.variants.length; m++) {
              var rv = r.variants[m];
              if (rv.size === ps && rv.available && (!r.hasColor || !r.color || rv.color === r.color)) { r.size = ps; changed = true; break; }
            }
          });
        }
        if (changed) update();
      };
      followPage();
      onPdpPick(followPage);
    }
  }

  /* ------------------------------------------------------------- bootstrap */

  function boot() {
    each(document.querySelectorAll('[data-btier]'), initTiers);
    each(document.querySelectorAll('[data-bpair]'), initPair);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
